/**
 * Pure logic behind the Box folders "Sync progress" column.
 *
 * Bender's `run` object reports a sync in two stages: listing Box folders, which queues batches
 * of files, then qualification, the stage that actually writes rows into the asset index. It
 * runs asynchronously and can lag by seconds, minutes, or 30-90 minutes on a redrive tail.
 * This module guards the untyped `run` JSON, then turns its batch counters into one derived
 * state so JSX never reasons about absent fields.
 *
 * Kept free of React and Meridian so the derivation table is simple to unit test. See
 * docs/system-specs/modules/box-folders.md (Sync progress) for the reasoning behind each
 * state, and Bender's docs/system-specs/modules/sync-target.md (Qualification Progress)
 * for the write side.
 */

import { DateTime } from 'luxon';
import {
  type SyncRunDto,
  type SyncTargetDto,
  SyncTargetStatuses,
} from 'src/types/sync-target.types';

export type BoxFolderProgressKind =
  | 'NEVER_SCANNED'
  | 'SCANNING'
  | 'NO_ASSETS'
  | 'SYNCING'
  | 'SYNCED';

export type BoxFolderProgress =
  /** No run has ever begun. Every freshly enrolled folder. */
  | { kind: 'NEVER_SCANNED' }
  /** A run is listing folders, or failed before queuing any batch. No denominator exists. */
  | { kind: 'SCANNING' }
  /** The run queued no batches: an empty folder, or everything excluded. */
  | { kind: 'NO_ASSETS'; filesQueued?: number }
  | { kind: 'SYNCED'; filesQueued?: number }
  | {
    kind: 'SYNCING';
    /** Clamped to `total`, so an over-shot counter never reads past complete. */
    qualified: number;
    total: number;
    /** Held inside `[1, 99]` while genuinely in progress — see `toPercent`. */
    percent: number;
    filesQueued?: number;
    lastQualifiedAt?: string;
  };

/**
 * Returns the target's `run` only when it is a plain object. The API slice passes Bender's JSON
 * through untyped, so a `null`, string, number, or array `run` is treated as absent.
 */
export function readRun (syncTarget: SyncTargetDto): SyncRunDto | undefined {
  const { run } = syncTarget;
  return typeof run === 'object' && run !== null && !Array.isArray(run) ? run : undefined;
}

/**
 * A counter is only trusted when it is a non-negative integer. Bender writes these with
 * `ADD` and plain `Number` schema fields, and they cross an untyped JSON boundary, so a
 * negative, fractional, `NaN`, or infinite value is treated as absent rather than rendered.
 */
function usable (value: number | undefined): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

/**
 * Percentage for an in-progress run, deliberately not a plain round.
 *
 * `Math.round(399 / 400 * 100)` is `100`, which would print "100% complete" beside
 * "Syncing assets"; `Math.floor(1 / 400 * 100)` is `0`, which would claim nothing had
 * finished when a batch demonstrably had. So the result is held inside `[1, 99]` whenever a
 * batch has finished but not all of them have, and the number never contradicts its label.
 *
 * Only ever called with `0 <= qualified < total` and `total >= 1`; the caller returns
 * `SYNCED` before reaching here, so there is deliberately no 100% case to handle.
 */
function toPercent (qualified: number, total: number): number {
  if (qualified <= 0) return 0;
  return Math.min(99, Math.max(1, Math.floor((qualified / total) * 100)));
}

/**
 * `filesQueued` is omitted when zero so the cell's rule stays uniform ("render the file line
 * iff `filesQueued !== undefined`") rather than a per-state condition. Zero only ever occurs
 * alongside `NO_ASSETS`, whose label already says it.
 */
function toFilesQueued (filesQueued: number | undefined): number | undefined {
  return usable(filesQueued) && filesQueued > 0 ? filesQueued : undefined;
}

/**
 * Derives what the Sync progress column should show for one enrolled folder.
 *
 * The branches are ordered and the first match wins. Two of them turn on the *absence* of a
 * field, which is load-bearing in both directions:
 *
 * - Bender sets `batchesQualified: 0` when every run starts, so an absent or malformed `run`, or
 *   an unusable numerator, proves no run ever began.
 * - Bender removes `batchesQueued` at run start and sets it once the first folder listing
 *   succeeds, so an absent denominator means a run is in flight (or failed before queuing),
 *   never zero.
 *
 * `status` is deliberately not read. Progress accrues in any status and continues after
 * `status` has returned to `ACTIVE`, so the two are orthogonal; a failed run surfaces through
 * the `ERROR` tag in the Status column, which is what keeps this column from duplicating it.
 */
export function deriveBoxFolderProgress (syncTarget: SyncTargetDto): BoxFolderProgress {
  const run = readRun(syncTarget);

  if (!run || !usable(run.batchesQualified)) {
    return { kind: 'NEVER_SCANNED' };
  }

  const { batchesQualified, batchesQueued, filesQueued, lastQualifiedAt } = run;

  if (!usable(batchesQueued)) {
    // A numerator may already have accrued here — Bender lets batches be consumed before the
    // run stamps its denominator — but a numerator without a denominator is not progress.
    return { kind: 'SCANNING' };
  }

  const renderedFilesQueued = toFilesQueued(filesQueued);

  if (batchesQueued === 0) {
    return { kind: 'NO_ASSETS', filesQueued: renderedFilesQueued };
  }

  const total = batchesQueued;
  // An SQS redelivery can push the numerator past the denominator.
  const qualified = Math.min(batchesQualified, total);

  if (qualified >= total) {
    return { kind: 'SYNCED', filesQueued: renderedFilesQueued };
  }

  return {
    kind: 'SYNCING',
    qualified,
    total,
    percent: toPercent(qualified, total),
    filesQueued: renderedFilesQueued,
    lastQualifiedAt,
  };
}

/**
 * Whether this folder's sync is still expected to move, i.e. whether the page should keep
 * asking for it.
 *
 * `status` is read here and only here. That is not folding progress into status: it decides
 * whether to keep *polling*, not what to *render*. Both status checks are load-bearing:
 *
 * - `ERROR`/`PAUSED` stop the polling. `fail()` clears neither counter and `complete()` never
 *   resets the numerator, so a run that died after `start()` reads as `SCANNING` forever and
 *   would otherwise be polled until the tab closed.
 * - `QUEUED`/`IN_PROGRESS` start it, regardless of what the counters derive to. `queue()`
 *   stamps the status without touching the counters, so between "Sync now" and the enumerator
 *   calling `start()` the counters still describe the *previous* run — typically reading as
 *   complete. Deriving from them alone would leave the page showing a finished sync beside a
 *   queued folder, with nothing scheduled to correct it.
 */
export function isBoxFolderSyncMoving (syncTarget: SyncTargetDto): boolean {
  const { status } = syncTarget;
  if (status === SyncTargetStatuses.ERROR || status === SyncTargetStatuses.PAUSED) {
    return false;
  }
  if (status === SyncTargetStatuses.QUEUED || status === SyncTargetStatuses.IN_PROGRESS) {
    return true;
  }

  const { kind } = deriveBoxFolderProgress(syncTarget);
  return kind === 'SYNCING' || kind === 'SCANNING';
}

/**
 * Relative time for the "last update" line, e.g. "12 minutes ago".
 *
 * Luxon's `toRelative`, not the `toRelativeCalendar` used by the row's date cells: this line
 * reports minutes-to-hours of worker activity, a scale at which day-granular output ("today")
 * says nothing. Returns `undefined` for an absent or unparseable timestamp so the caller can
 * omit the line entirely rather than render a fallback string.
 */
export function relativeFromNow (iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const dateTime = DateTime.fromISO(iso, { zone: 'utc' });
  return dateTime.isValid ? (dateTime.toRelative() ?? undefined) : undefined;
}
